/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
/**
 * Shared modal accessibility helpers for custom overlay dialogs.
 * Used by section-specific modal scripts across the theme.
 */
(function () {
  "use strict";

  var FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  function normalizeRoots(roots) {
    if (!roots) return [];
    return Array.isArray(roots) ? roots.filter(Boolean) : [roots];
  }

  function isVisible(el) {
    if (!el || el.nodeType !== 1) return false;

    // A focusable descendant is unavailable when any ancestor is hidden or
    // inert. Checking the full chain also covers controls inside a hidden tab
    // panel, rather than relying on the root's own computed style only.
    var current = el;
    while (current && current.nodeType === 1) {
      if (
        current.hasAttribute("hidden") ||
        current.hasAttribute("inert") ||
        current.getAttribute("aria-hidden") === "true"
      ) {
        return false;
      }
      var style = window.getComputedStyle(current);
      if (style.display === "none" || style.visibility === "hidden") return false;
      current = current.parentElement;
    }
    return true;
  }

  function getFocusable(roots) {
    var items = [];
    normalizeRoots(roots).forEach(function (root) {
      var candidates = [];
      if (root.matches && root.matches(FOCUSABLE_SELECTOR)) candidates.push(root);
      candidates = candidates.concat(Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)));
      candidates.forEach(function (el) {
        if (isVisible(el) && items.indexOf(el) === -1) {
          items.push(el);
        }
      });
    });
    return items;
  }

  function trapFocus(roots, e) {
    if (e.key !== "Tab") return;
    var focusable = getFocusable(roots);
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;
    var containers = normalizeRoots(roots);
    var activeInside = containers.some(function (container) {
      return container === active || container.contains(active);
    });

    if (e.shiftKey) {
      if (active === first || !activeInside) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !activeInside) {
      e.preventDefault();
      first.focus();
    }
  }

  var inertLockCount = 0;
  var inertedElements = new Set();
  var inertSnapshots = new Map();
  var modalStack = [];

  function containsAnyRoot(el, roots) {
    return roots.some(function (root) {
      return el === root || el.contains(root);
    });
  }

  function markInert(el) {
    if (!el) return;
    if (!inertSnapshots.has(el)) {
      inertSnapshots.set(el, {
        ariaHidden: {
          present: el.hasAttribute("aria-hidden"),
          value: el.getAttribute("aria-hidden"),
        },
        inert: {
          present: el.hasAttribute("inert"),
          value: el.getAttribute("inert"),
        },
      });
    }
    el.setAttribute("aria-hidden", "true");
    el.setAttribute("inert", "");
    inertedElements.add(el);
  }

  function restoreInert(el) {
    var snapshot = inertSnapshots.get(el);
    if (!snapshot) return;

    if (snapshot.ariaHidden.present) {
      el.setAttribute("aria-hidden", snapshot.ariaHidden.value);
    } else {
      el.removeAttribute("aria-hidden");
    }
    if (snapshot.inert.present) {
      el.setAttribute("inert", snapshot.inert.value);
    } else {
      el.removeAttribute("inert");
    }
    inertSnapshots.delete(el);
  }

  function applyInertExcluding(container, exceptRoots) {
    var roots = normalizeRoots(exceptRoots);
    Array.from(container.children).forEach(function (child) {
      // Never inert the modal root itself: stop the walk so its whole subtree
      // stays interactive and visible to assistive technology.
      if (roots.indexOf(child) !== -1) {
        return;
      }
      if (containsAnyRoot(child, roots)) {
        applyInertExcluding(child, roots);
      } else {
        markInert(child);
      }
    });
  }

  function clearAllInert() {
    inertedElements.forEach(restoreInert);
    inertedElements.clear();
  }

  function unmarkInert(el) {
    if (!el) return;
    if (inertedElements.has(el)) {
      restoreInert(el);
      inertedElements.delete(el);
    }
  }

  function setPageInert(active, modalRoots) {
    var main = document.getElementById("main");
    if (!main) return;
    if (active) {
      inertLockCount += 1;
      var roots = normalizeRoots(modalRoots);
      // A dialog opened from inside another dialog (or reopened) may have been
      // inerted as background earlier: the active dialog must stay interactive.
      roots.forEach(unmarkInert);
      if (inertLockCount === 1) {
        var modalInsideMain = roots.some(function (root) {
          return main.contains(root);
        });
        if (modalInsideMain) {
          applyInertExcluding(main, roots);
        } else {
          markInert(main);
        }
      }
      return;
    }
    inertLockCount = Math.max(0, inertLockCount - 1);
    if (inertLockCount === 0) {
      clearAllInert();
    }
  }

  function setRootsHidden(roots, hidden) {
    normalizeRoots(roots).forEach(function (root) {
      root.setAttribute("aria-hidden", hidden ? "true" : "false");
    });
  }

  var globalTrapHandler = function (e) {
    if (modalStack.length === 0) return;
    trapFocus(modalStack[modalStack.length - 1].roots, e);
  };

  /**
   * @param {Object} config
   * @param {Element|Element[]} config.roots - Modal root element(s) for focus trap
   * @param {Element} [config.trigger] - Element to restore focus to on close
   * @param {Element|string} [config.focusTarget] - Initial focus target
   * @param {boolean} [config.inert=true] - Mark page background as inert while open
   * @returns {{ roots: Element[], trapHandler: Function, lastFocused: Element|null }}
   */
  function activate(config) {
    var roots = normalizeRoots(config.roots);
    var state = {
      roots: roots,
      lastFocused: config.trigger || document.activeElement,
      trapHandler: globalTrapHandler,
      previous: modalStack[modalStack.length - 1] || null,
    };

    if (config.inert !== false) setPageInert(true, roots);
    // Apply this after page isolation so a nested root that was previously
    // inerted is restored to its active state rather than its old hidden one.
    setRootsHidden(roots, false);

    modalStack.push(state);
    if (modalStack.length === 1) {
      document.addEventListener("keydown", globalTrapHandler);
    }

    requestAnimationFrame(function () {
      var focusTarget = config.focusTarget;
      if (typeof focusTarget === "string") {
        focusTarget = roots[0] && roots[0].querySelector(focusTarget);
      }
      var focusable = getFocusable(roots);
      var target = focusTarget && isVisible(focusTarget) ? focusTarget : focusable[0];
      if (target && typeof target.focus === "function") {
        target.focus();
      }
    });

    return state;
  }

  function deactivate(state) {
    if (!state) return;
    var index = modalStack.indexOf(state);
    if (index !== -1) {
      modalStack.splice(index, 1);
    }
    if (modalStack.length === 0) {
      document.removeEventListener("keydown", globalTrapHandler);
    }
    // Modal roots use aria-hidden as their lifecycle state. Keep the existing
    // caller contract: every active root is hidden again when deactivated.
    setRootsHidden(state.roots, true);
    setPageInert(false);
    var returnTarget = state.lastFocused;
    if (!isVisible(returnTarget) && state.previous) {
      var previousFocusable = getFocusable(state.previous.roots);
      returnTarget = previousFocusable[0] || state.previous.roots[0];
    }
    if (returnTarget && typeof returnTarget.focus === "function") {
      try {
        returnTarget.focus();
      } catch (err) {
        /* no-op */
      }
    }
  }

  window.UnifiberModalA11y = {
    FOCUSABLE_SELECTOR: FOCUSABLE_SELECTOR,
    getFocusable: getFocusable,
    trapFocus: trapFocus,
    setPageInert: setPageInert,
    activate: activate,
    deactivate: deactivate,
  };
})();
